import { watchlistService } from '@/services/watchlist-service'
import { instagramService } from '@/services/instagram-service'

export interface RefreshResult {
  success: boolean
  message: string
  newWatchlistId?: string
  validUuids: string[]
  invalidUuids: string[]
}

export interface ValidationResult {
  validUuids: string[]
  invalidUuids: string[]
}

/**
 * Validates a list of account UUIDs by checking if they still exist in the analytics table
 */
export async function validateAccountUuids(uuids: string[]): Promise<ValidationResult> {
  const validUuids: string[] = []
  const invalidUuids: string[] = []

  // Validate each UUID sequentially to properly catch errors
  for (const uuid of uuids) {
    try {
      await instagramService.getAccountAnalytics(uuid)
      validUuids.push(uuid)
    } catch (error: any) {
      // Check if it's a 404 (account deleted) or other error
      invalidUuids.push(uuid)
    }
  }

  return { validUuids, invalidUuids }
}

/**
 * Refreshes a watchlist by recreating it with the same accounts that still exist
 */
export async function refreshWatchlist(watchlistId: string): Promise<RefreshResult> {
  try {
    // Get current watchlist details
    const watchlistData = await watchlistService.getWatchlist(watchlistId)
    
    // Extract account UUIDs from the accounts array
    const currentUuids = watchlistData.accounts?.map(account => account.id) || []
    
    if (currentUuids.length === 0) {
      return {
        success: false,
        message: "No accounts found in the current watchlist",
        validUuids: [],
        invalidUuids: []
      }
    }

    // Validate which UUIDs still exist
    const { validUuids, invalidUuids } = await validateAccountUuids(currentUuids)

    // Handle case where all accounts are invalid
    if (validUuids.length === 0) {
      return {
        success: false,
        message: "All accounts in this watchlist have been deleted and are no longer available",
        validUuids: [],
        invalidUuids
      }
    }

    // Create new watchlist with valid accounts - keep same name and description
    const response = await watchlistService.createWatchlist({
      name: watchlistData.name,
      description: watchlistData.description,
      account_uuids: validUuids
    })

    // Delete the old watchlist after successful creation
    try {
      await watchlistService.deleteWatchlist(watchlistId)
    } catch (deleteError) {
      console.warn('Failed to delete old watchlist:', deleteError)
      // Continue anyway since new watchlist was created successfully
    }

    // Determine success message based on whether any accounts were removed
    let message: string
    if (invalidUuids.length === 0) {
      message = `Watchlist "${watchlistData.name}" refreshed successfully with all ${validUuids.length} accounts`
    } else {
      message = `Watchlist "${watchlistData.name}" refreshed with ${validUuids.length} accounts (${invalidUuids.length} removed accounts were no longer available)`
    }

    return {
      success: true,
      message,
      newWatchlistId: response.watchlist_id,
      validUuids,
      invalidUuids
    }

  } catch (error) {
    console.error('Failed to refresh watchlist:', error)
    
    // Handle specific error cases
    if (error instanceof Error) {
      if (error.message.includes('404')) {
        return {
          success: false,
          message: "Watchlist not found or no longer accessible",
          validUuids: [],
          invalidUuids: []
        }
      }
      if (error.message.includes('No valid accounts found')) {
        return {
          success: false,
          message: "All accounts in this watchlist are no longer available",
          validUuids: [],
          invalidUuids: []
        }
      }
    }

    return {
      success: false,
      message: "Failed to refresh watchlist. Please try again later.",
      validUuids: [],
      invalidUuids: []
    }
  }
} 